import { useEffect } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { useNotifier } from "react-headless-notifier";
import { useFieldArray, useForm } from "react-hook-form";

import {
  Button,
  Input,
  Table,
  Toggle,
  Multiselect,
  NotifierNotification,
  UnsavedChangesPrompt,
  Select,
  SelectOption,
  FormGroup,
} from "@mwi/ui";

import {
  useGetPracticeFeaturesQuery,
  useGetSitesQuery,
  useGetTaskListsQuery,
  useUpdatePracticeFeaturesMutation,
} from "api";
import { RootState } from "store";
import useErrorHandler from "hooks/useErrorHandler";
import useTitle from "hooks/useTitle";

type Inputs = {
  registrations_blocked: boolean;
  messaging_blocked: boolean;
  appointments_choose_vet: boolean;
  registrations_notify: boolean;
  registration_notification_email: {
    email: string;
    location_id: string;
  }[];
  appointments_blocked: boolean;
  appointments_blocked_specific: boolean;
  cancellations_blocked: boolean;
  cancellations_blocked_specific: boolean;
  cancellation_message?: string;
  prescription_requests_blocked: boolean;
  prescription_requests_blocked_specific: boolean;
  appointments_blocked_locations: string[];
  cancellations_blocked_locations: string[];
  prescription_requests_blocked_locations: string[];
  selected_prescription_request_diaries: {
    diary_id: string;
    location_id: string;
  }[];
};

const PracticeFeaturesPage = (): JSX.Element => {
  useTitle("Features");
  const errorHandler = useErrorHandler();

  const { notify } = useNotifier();

  const { data, isLoading } = useGetPracticeFeaturesQuery();

  const { data: locations } = useGetSitesQuery();

  const { data: taskLists } = useGetTaskListsQuery();

  const user = useSelector((state: RootState) => state.auth.user);

  const [
    updatePractice,
    { isLoading: isSaving, isSuccess: updatedPractice, error },
  ] = useUpdatePracticeFeaturesMutation();

  const {
    control,
    setValue,
    watch,
    getValues,
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setError,
  } = useForm<Inputs>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "selected_prescription_request_diaries", // unique name for your Field Array
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "registration_notification_email",
  });

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
      notify(
        <NotifierNotification
          type="error"
          title="Unable to update"
          message="There was a problem updating practice features"
        />,
      );
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      /*
        Manually reset the field as useForm needs to have default values to
        properly manage isDirty field
      */
      reset({
        registrations_blocked: data.registrations_blocked,
        messaging_blocked: data.messaging_blocked,
        appointments_choose_vet: data.appointments_choose_vet,
        registrations_notify: data.registrations_notify,
        registration_notification_email: data.registration_notification_email,
        appointments_blocked: data.appointments_blocked,
        appointments_blocked_specific: data.appointments_blocked_specific,
        cancellations_blocked: data.cancellations_blocked,
        cancellations_blocked_specific: data.cancellations_blocked_specific,
        cancellation_message: data.cancellation_message ?? "",
        prescription_requests_blocked: data.prescription_requests_blocked,
        prescription_requests_blocked_specific:
          data.prescription_requests_blocked_specific,
        appointments_blocked_locations: data.appointments_blocked_locations,
        cancellations_blocked_locations: data.cancellations_blocked_locations,
        prescription_requests_blocked_locations:
          data.prescription_requests_blocked_locations,
        selected_prescription_request_diaries:
          data.selected_prescription_request_diaries,
      });
    }
  }, [data]);

  useEffect(() => {
    if (data && locations && fields.length === locations.length) {
      append(
        locations.reduce(
          (p, c) => [
            ...p,
            {
              location_id: c.id,
              diary_id:
                data.selected_prescription_request_diaries.find(
                  (d) => d.location_id === c.id,
                )?.diary_id ?? "",
            },
          ],
          [] as { location_id: string; diary_id: string }[],
        ),
      );
    }
  }, [data, locations]);

  // Cleanup field array on each render
  fields.forEach((field, index) => {
    if (
      !field.location_id ||
      fields.findIndex((f) => f.location_id === field.location_id) !== index
    ) {
      remove(index);
    }
  });

  useEffect(() => {
    if (data && locations && emailFields.length === locations.length) {
      appendEmail(
        locations.reduce(
          (p, c) => [
            ...p,
            {
              location_id: c.id,
              email:
                data.registration_notification_email.find(
                  (d) => d.location_id === c.id,
                )?.email ?? "",
            },
          ],
          [] as { location_id: string; email: string }[],
        ),
      );
    }
  }, [data, locations]);

  // Cleanup field array on each render
  emailFields.forEach((field, index) => {
    if (
      !field.location_id ||
      emailFields.findIndex((f) => f.location_id === field.location_id) !==
        index
    ) {
      removeEmail(index);
    }
  });

  useEffect(() => {
    if (getValues("appointments_blocked")) {
      setValue(
        "appointments_blocked_specific",
        !getValues("appointments_blocked"),
      );
    }
  }, [watch("appointments_blocked")]);

  useEffect(() => {
    if (getValues("appointments_blocked_specific")) {
      setValue(
        "appointments_blocked",
        !getValues("appointments_blocked_specific"),
      );
    }
  }, [watch("appointments_blocked_specific")]);

  useEffect(() => {
    if (getValues("cancellations_blocked")) {
      setValue(
        "cancellations_blocked_specific",
        !getValues("cancellations_blocked"),
      );
    }
  }, [watch("cancellations_blocked")]);

  useEffect(() => {
    if (getValues("cancellations_blocked_specific")) {
      setValue(
        "cancellations_blocked",
        !getValues("cancellations_blocked_specific"),
      );
    }
  }, [watch("cancellations_blocked_specific")]);

  useEffect(() => {
    if (getValues("prescription_requests_blocked")) {
      setValue(
        "prescription_requests_blocked_specific",
        !getValues("prescription_requests_blocked"),
      );
    }
  }, [watch("prescription_requests_blocked")]);

  useEffect(() => {
    if (getValues("prescription_requests_blocked_specific")) {
      setValue(
        "prescription_requests_blocked",
        !getValues("prescription_requests_blocked_specific"),
      );
    }
  }, [watch("prescription_requests_blocked_specific")]);

  useEffect(() => {
    if (updatedPractice) {
      notify(
        <NotifierNotification
          title="Practice Updated"
          message="Practices features have been updated"
        />,
      );
    }
  }, [updatedPractice]);

  return (
    <UnsavedChangesPrompt isDirty={isDirty}>
      <form
        onSubmit={handleSubmit(updatePractice)}
        className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6"
      >
        <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
          <p className="font-semibold">
            Manage the availability of different features across the platform
          </p>
          <p>Turn features On/Off in case of exceptional situations</p>
        </div>

        <div className="mb-8 col-span-full">
          <Table loading={isLoading}>
            <Table.Head>
              <Table.Row>
                <Table.Item as="th" className="w-4/5">
                  Registration
                </Table.Item>
                <Table.Item as="th">Active</Table.Item>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Block new users from registering on the platform
                </Table.Item>
                <Table.Item>
                  <Toggle control={control} name="registrations_blocked" />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Email when a new user registers on the platform
                </Table.Item>
                <Table.Item>
                  <Toggle control={control} name="registrations_notify" />
                </Table.Item>
              </Table.Row>

              <Table.Row>
                <Table.Item colSpan={2}>
                  {locations?.map((location, index) => (
                    <div
                      className={
                        getValues("registrations_notify")
                          ? "flex mb-2"
                          : "flex mb-2 opacity-50"
                      }
                      key={emailFields[index]?.id ?? index}
                    >
                      <div className="w-3/5 pt-4 whitespace-normal">
                        {location.name} email:
                      </div>
                      <div className="w-4/5">
                        <Input
                          {...register(
                            `registration_notification_email.${emailFields.findIndex((rne) => rne.location_id === location.id)}.email`,
                          )}
                          placeHolder="Enter email address"
                          className="border-none !rounded shadow-none !bg-grey-lightest tablet:!text-base"
                        />
                      </div>
                    </div>
                  ))}
                </Table.Item>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        {/* @TODO: remove REACT_APP_ENABLE_COMMS_TOGGLE once comms is released to prod */}
        {user?.show_practice_switcher &&
          process.env.REACT_APP_ENABLE_COMMS_TOGGLE === "true" && (
            <div className="mb-8 col-span-full">
              <Table loading={isLoading}>
                <Table.Head>
                  <Table.Row>
                    <Table.Item as="th" className="w-4/5">
                      Messaging
                    </Table.Item>
                    <Table.Item as="th">Active</Table.Item>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  <Table.Row>
                    <Table.Item className="font-semibold">
                      Block messaging for all sites
                    </Table.Item>
                    <Table.Item>
                      <Toggle control={control} name="messaging_blocked" />
                    </Table.Item>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          )}

        <div className="mb-8 col-span-full">
          <Table loading={isLoading}>
            <Table.Head borderTop>
              <Table.Row>
                <Table.Item as="th" className="w-4/5">
                  Booking appointments
                </Table.Item>
                <Table.Item as="th">Active</Table.Item>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Allow pet owners to choose a specific vet when booking
                  appointments
                </Table.Item>
                <Table.Item>
                  <Toggle control={control} name="appointments_choose_vet" />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Block pet owners from booking appointments for all sites
                </Table.Item>
                <Table.Item>
                  <Toggle control={control} name="appointments_blocked" />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Block pet owners from booking appointments only for specific
                  sites
                </Table.Item>
                <Table.Item>
                  <Toggle
                    control={control}
                    name="appointments_blocked_specific"
                  />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="pt-0" colSpan={1}>
                  <Multiselect
                    name="appointments_blocked_locations"
                    control={control}
                    placeholder="Select one or more locations"
                    size="medium"
                    buttonClassName="border-none !rounded shadow-none !bg-grey-lightest tablet:!text-base"
                  >
                    {locations?.map((location) => (
                      <Multiselect.Option key={location.id} value={location.id}>
                        {location.name}
                      </Multiselect.Option>
                    ))}
                  </Multiselect>
                </Table.Item>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        <div className="mb-8 col-span-full">
          <Table loading={isLoading}>
            <Table.Head borderTop>
              <Table.Row>
                <Table.Item as="th" className="w-4/5">
                  Cancelling appointments
                </Table.Item>
                <Table.Item as="th">Active</Table.Item>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Block pet owners from cancelling appointments for all sites
                </Table.Item>
                <Table.Item>
                  <Toggle control={control} name="cancellations_blocked" />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Block pet owners from cancelling appointments only for
                  specific sites
                </Table.Item>
                <Table.Item>
                  <Toggle
                    control={control}
                    name="cancellations_blocked_specific"
                  />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="pt-0" colSpan={1}>
                  <Multiselect
                    name="cancellations_blocked_locations"
                    control={control}
                    placeholder="Select one or more locations"
                    size="medium"
                    buttonClassName="border-none !rounded shadow-none !bg-grey-lightest tablet:!text-base"
                  >
                    {locations?.map((location) => (
                      <Multiselect.Option key={location.id} value={location.id}>
                        {location.name}
                      </Multiselect.Option>
                    ))}
                  </Multiselect>
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="font-semibold">
                  Write a short message that customers will see when trying to
                  cancel
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item colSpan={1} className="pt-0">
                  <Input
                    {...register("cancellation_message")}
                    placeHolder="No message"
                    className="border-none !rounded shadow-none !bg-grey-lightest tablet:!text-base"
                  />
                </Table.Item>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        <div className="mb-8 col-span-full">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Item as="th" className="w-4/5">
                  Prescription Requests
                </Table.Item>
                <Table.Item as="th">Active</Table.Item>
              </Table.Row>
            </Table.Head>

            <Table.Body>
              <Table.Row>
                <Table.Item>
                  <b>
                    Select which Merlin diary prescription requests are to be
                    placed for specific sites
                  </b>
                </Table.Item>
              </Table.Row>

              <Table.Row>
                <Table.Item>
                  {locations?.map((location, index) => (
                    <div className="flex" key={fields[index]?.id ?? index}>
                      <div className="w-2/5 mt-3 whitespace-normal">
                        {location.name}
                      </div>
                      <div className="w-3/5">
                        <FormGroup
                          error={
                            errors.selected_prescription_request_diaries?.[
                              index
                            ]?.diary_id?.message
                          }
                        >
                          <Select
                            name={`selected_prescription_request_diaries.${data?.selected_prescription_request_diaries.findIndex((d) => d.location_id === location.id)}.diary_id`}
                            placeholder="Select diary for prescription requests"
                            size="small"
                            control={control}
                            buttonClassName={classNames(
                              "!px-4 !rounded",
                              " !text-base",
                              "shadow-none !bg-grey-lightest !border-grey-lightest",
                              "!focus:border !focus:border-primary",
                            )}
                          >
                            {Object.entries(taskLists ?? {})
                              ?.find(
                                ([locationId]) => locationId === location.id,
                              )?.[1]
                              ?.map((diary) => (
                                <SelectOption key={diary.id} value={diary.id}>
                                  {`${diary.first_name} ${diary.last_name}`}
                                </SelectOption>
                              ))}
                          </Select>
                        </FormGroup>
                      </div>
                    </div>
                  ))}
                </Table.Item>
              </Table.Row>

              <Table.Row>
                <Table.Item>
                  <b>
                    Block pet owners from requesting prescriptions for all sites
                  </b>
                </Table.Item>
                <Table.Item>
                  <Toggle
                    control={control}
                    name="prescription_requests_blocked"
                  />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item>
                  <b>
                    Block pet owners from requesting prescriptions only for
                    specific sites
                  </b>
                </Table.Item>
                <Table.Item>
                  <Toggle
                    control={control}
                    name="prescription_requests_blocked_specific"
                  />
                </Table.Item>
              </Table.Row>
              <Table.Row>
                <Table.Item className="pt-0" colSpan={1}>
                  <Multiselect
                    name="prescription_requests_blocked_locations"
                    control={control}
                    placeholder="Select one or more locations"
                    size="medium"
                    buttonClassName="border-none !rounded shadow-none !bg-grey-lightest tablet:!text-base"
                  >
                    {locations?.map((location) => (
                      <Multiselect.Option key={location.id} value={location.id}>
                        {location.name}
                      </Multiselect.Option>
                    ))}
                  </Multiselect>
                </Table.Item>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        <div className="col-span-full max-w-[185px]">
          <Button block type="submit" loading={isSaving}>
            Save
          </Button>
        </div>
      </form>
    </UnsavedChangesPrompt>
  );
};

export default PracticeFeaturesPage;
